import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaEye, FaEyeSlash, FaCrown, FaTrashAlt, FaCamera, FaRobot, FaLaptop, FaMobileAlt, FaTrash, FaUser, FaBuilding, FaUniversity, FaBell, FaShieldAlt, FaUserCog, FaSun, FaMoon, FaPaintBrush, FaClock, FaGlobe, FaDownload, FaChevronDown, FaCheck, FaDesktop, FaStop, FaVolumeUp } from 'react-icons/fa';
import { updateProfile, updateCompany, updateBank, updateNotifications, saveProfile, saveCompany, saveBank, saveNotifications, loadSettings } from '../redux/slices/settingsSlice';
import { fetchAiPreferences, saveAiPreferences } from '../redux/slices/aiSlice';
import Modal from '../components/Modal/Modal';
import PlanUpgradeModal from '../components/PlanUpgradeModal/PlanUpgradeModal';
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay';
import useTrialStatus from '../hooks/useTrialStatus';
import './Settings.scss';
import { useNavigate } from 'react-router-dom';
import { logout, logoutUser, loginSuccess, updateUser } from '../redux/slices/authSlice';
import { API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import { isNotificationSoundMuted, setNotificationSoundMuted } from '../utils/alertSound';
import { subscribeToPush, unsubscribeFromPush, getCurrentPushSubscription, isPushSupported } from '../utils/pushNotifications';
import showToast from '../utils/toast';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const MAX_REMINDER_MINUTES = 15 * 24 * 60;
const DEFAULT_REMINDER_MINUTES = 1440;
const REMINDER_PRESET_OPTIONS = [
  { label: '2 hours before', value: 120 },
  { label: '12 hours before', value: 720 },
  { label: '24 hours before', value: 1440 },
  { label: '2 days before', value: 2880 },
];

// Preview lines per conversation tone + language + voice identity.
// English uses Mivan (male) / Mira (female); Kannada uses ಮಿವಾನ್ / ಮೀರಾ.
// Each line is written IN the tone it demonstrates so the preview shows
// both the wording style and the voice modulation (rate/pitch) together.
const AI_VOICE_PREVIEWS = {
  friendly: {
    'en-IN': {
      male: "Hi, I'm Mivan, your personal assistant. So glad to see you — how can I help you today?",
      female: "Hi, I'm Mira, your personal assistant. So glad to see you — how can I help you today?",
    },
    'kn-IN': {
      male: 'ನಮಸ್ಕಾರ, ನಾನು ಮಿವಾನ್, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ. ನಿಮ್ಮನ್ನು ನೋಡಿ ತುಂಬಾ ಸಂತೋಷ — ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?',
      female: 'ನಮಸ್ಕಾರ, ನಾನು ಮೀರಾ, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ. ನಿಮ್ಮನ್ನು ನೋಡಿ ತುಂಬಾ ಸಂತೋಷ — ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?',
    },
  },
  professional: {
    'en-IN': {
      male: "Hello, I'm Mivan, your personal assistant. How may I assist you today?",
      female: "Hello, I'm Mira, your personal assistant. How may I assist you today?",
    },
    'kn-IN': {
      male: 'ನಮಸ್ಕಾರ, ನಾನು ಮಿವಾನ್, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ. ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?',
      female: 'ನಮಸ್ಕಾರ, ನಾನು ಮೀರಾ, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ. ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?',
    },
  },
  humorous: {
    'en-IN': {
      male: "Hey there! I'm Mivan, your personal assistant. Ready to turn your boring data into brilliant answers — what's up?",
      female: "Hey there! I'm Mira, your personal assistant. Ready to turn your boring data into brilliant answers — what's up?",
    },
    'kn-IN': {
      male: 'ಹೇ! ನಾನು ಮಿವಾನ್, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ. ನಿಮ್ಮ ಕೆಲಸವನ್ನು ಮೋಜಿನಿಂದ ಸುಲಭಗೊಳಿಸೋಣ — ಹೇಳಿ, ಏನು ಬೇಕು?',
      female: 'ಹೇ! ನಾನು ಮೀರಾ, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ. ನಿಮ್ಮ ಕೆಲಸವನ್ನು ಮೋಜಿನಿಂದ ಸುಲಭಗೊಳಿಸೋಣ — ಹೇಳಿ, ಏನು ಬೇಕು?',
    },
  },
  minimalist: {
    'en-IN': {
      male: "I'm Mivan. Ask about your events, bills, or team.",
      female: "I'm Mira. Ask about your events, bills, or team.",
    },
    'kn-IN': {
      male: 'ನಾನು ಮಿವಾನ್. ನಿಮ್ಮ ಈವೆಂಟ್, ಬಿಲ್ ಅಥವಾ ತಂಡದ ಬಗ್ಗೆ ಕೇಳಿ.',
      female: 'ನಾನು ಮೀರಾ. ನಿಮ್ಮ ಈವೆಂಟ್, ಬಿಲ್ ಅಥವಾ ತಂಡದ ಬಗ್ಗೆ ಕೇಳಿ.',
    },
  },
  enthusiastic: {
    'en-IN': {
      male: "Hi! I'm Mivan, your personal assistant! I'm so excited to help you today — let's get started!",
      female: "Hi! I'm Mira, your personal assistant! I'm so excited to help you today — let's get started!",
    },
    'kn-IN': {
      male: 'ನಮಸ್ಕಾರ! ನಾನು ಮಿವಾನ್, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ! ನಿಮಗೆ ಸಹಾಯ ಮಾಡಲು ನನಗೆ ತುಂಬಾ ಉತ್ಸಾಹವಾಗಿದೆ — ಪ್ರಾರಂಭಿಸೋಣ!',
      female: 'ನಮಸ್ಕಾರ! ನಾನು ಮೀರಾ, ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕ! ನಿಮಗೆ ಸಹಾಯ ಮಾಡಲು ನನಗೆ ತುಂಬಾ ಉತ್ಸಾಹವಾಗಿದೆ — ಪ್ರಾರಂಭಿಸೋಣ!',
    },
  },
};

const getAiPreviewLine = (tone, speechLanguage, assistantVoice) => {
  const t = AI_VOICE_PREVIEWS[tone] ? tone : 'professional';
  const lang = speechLanguage === 'kn-IN' ? 'kn-IN' : 'en-IN';
  const voice = assistantVoice === 'male' ? 'male' : 'female';
  return AI_VOICE_PREVIEWS[t][lang][voice];
};

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/svg+xml',
];

const validateImageFile = (file) => {
  if (!file) return false;
  if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
    showToast('Invalid file type. Allowed formats: jpg, png, webp, gif, avif, svg.', { type: 'error', duration: 5000 });
    return false;
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    showToast(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 5 MB.`, { type: 'error', duration: 5000 });
    return false;
  }
  return true;
};

const clampReminderMinutes = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_REMINDER_MINUTES;
  return Math.min(MAX_REMINDER_MINUTES, Math.max(1, Math.round(parsed)));
};

const convertToMinutes = (value, unit) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_REMINDER_MINUTES;
  if (unit === 'days') return clampReminderMinutes(numeric * 24 * 60);
  if (unit === 'hours') return clampReminderMinutes(numeric * 60);
  return clampReminderMinutes(numeric);
};

const formatMinutesToCustomParts = (minutes) => {
  const safeMinutes = clampReminderMinutes(minutes);

  if (safeMinutes % (24 * 60) === 0) {
    return { value: safeMinutes / (24 * 60), unit: 'days' };
  }
  if (safeMinutes % 60 === 0) {
    return { value: safeMinutes / 60, unit: 'hours' };
  }
  return { value: safeMinutes, unit: 'minutes' };
};

const CustomSelect = ({ value, onChange, options, placeholder, id, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const boxRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const currentIndex = options.findIndex((o) => o.value === value);

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setOpen(true);
      setHighlighted(currentIndex);
      return;
    }
    if (!open) return;
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h >= options.length - 1 ? 0 : h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? options.length - 1 : h - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (highlighted >= 0) {
        onChange(options[highlighted].value);
        setOpen(false);
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const selectOption = (opt) => {
    if (disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  return (
    <div
      className={`settings__select-wrap ${open ? 'settings__select-wrap--open' : ''} ${disabled ? 'settings__select-wrap--disabled' : ''}`}
      ref={boxRef}
    >
      <button
        type="button"
        id={id}
        className="settings__select"
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={id}
        disabled={disabled}
        aria-disabled={disabled}
      >
        <span className="settings__select-value">{selected ? selected.label : (placeholder || 'Select...')}</span>
        <FaChevronDown className="settings__select-chevron" />
      </button>
      {open && (
        <ul className="settings__select-menu" role="listbox">
          {options.map((opt, i) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`settings__select-option ${opt.value === value ? 'settings__select-option--selected' : ''} ${highlighted === i ? 'settings__select-option--highlight' : ''}`}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => selectOption(opt)}
            >
              <span>{opt.label}</span>
              {opt.value === value && <FaCheck className="settings__select-check" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Settings = () => {
  const dispatch = useDispatch();
  const settingsState = useSelector((state) => state.settings);
  const aiState = useSelector((state) => state.ai);
  const { language, setLanguage, t } = useLanguage();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  // Read authenticated user from Redux or fall back to session storage
  const authUser = useSelector((state) => state.auth && state.auth.user) || session.getUser();
  
  // Plan-based restrictions
  const { isGoldPlan, isOnTrial, isSilverPlan, planTier } = useTrialStatus();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [restrictedFeatureName, setRestrictedFeatureName] = useState('');
  
  // Cards restricted to Gold plan only (Free trial and Silver plan cannot access)
  const isRestricted = !isGoldPlan;
  
  // Super Admin card restricted by role (admin/superadmin only)
  const isAdmin = authUser?.role === 'admin' || authUser?.role === 'superadmin';
  const isSuperAdminRestricted = !isAdmin;
  
  // AI Preferences state — includes calm confident voice (male/female)
  const [aiPrefs, setAiPrefs] = useState({
    tone: 'professional',
    speechLanguage: 'en-IN',
    assistantVoice: 'female',
  });
  
  const handleRestrictedCardClick = (featureName) => {
    if (isRestricted) {
      setRestrictedFeatureName(featureName);
      setShowUpgradeModal(true);
    }
  };
  
  useEffect(() => {
    dispatch(loadSettings());
    dispatch(fetchAiPreferences());
  }, [dispatch]);

  const uploadFile = async (file, endpoint = '/uploads') => {
    if (!file) return null;
    const formData = new FormData();
    const isReplacementEndpoint = [
      '/uploads/profile-image',
      '/uploads/company-logo',
      '/uploads/upi-qr',
    ].includes(endpoint);
    formData.append(isReplacementEndpoint ? 'file' : 'files', file);
    const token = session.getToken();
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (res.ok) {
      const data = await res.json();
      if (isReplacementEndpoint) {
        return data.imageUrl || null;
      }
      return data.files?.[0]?.url || null;
    }
    // Handle specific HTTP error codes with user-friendly messages
    if (res.status === 413) {
      console.error('Upload failed', res.status, 'Request Entity Too Large');
      const err = new Error('The server rejected the upload (request too large). Supported formats: JPG, PNG, WEBP, GIF, AVIF, SVG — max 5 MB. If your file is within limits, please contact support.');
      err.isUploadError = true;
      err.statusCode = 413;
      throw err;
    }
    // Parse server error message when available
    let serverMessage = '';
    try {
      const errData = await res.json();
      serverMessage = errData.message || errData.error || '';
    } catch (_) {}
    console.error('Upload failed', res.status, serverMessage);
    const err = new Error(serverMessage || 'Image upload failed. Please try again.');
    err.isUploadError = true;
    err.statusCode = res.status;
    throw err;
  };

  const [activeModal, setActiveModal] = useState(null);
  const [profile, setProfile] = useState({ fullName: '', email: '', phone: '', designation: '', avatarPreview: null });
  const [company, setCompany] = useState({ name: '', email: '', phone: '', address: '', logoPreview: null });
  // Company logo preview mode: 'square' (1:1) or 'landscape' (16:9)
  const [companyLogoMode, setCompanyLogoMode] = useState('square');
  const [bank, setBank] = useState({ bankName: '', accountHolder: '', accountNumber: '', branch: '', ifsc: '', address: '', upiId: '', upiQrPreview: null });
  const [notifications, setNotifications] = useState({
    overall: true,
    events: true,
    eventRemindersEnabled: true,
    eventReminderLeadMinutes: DEFAULT_REMINDER_MINUTES,
    projectRemindersEnabled: true,
    projectReminderLeadMinutes: DEFAULT_REMINDER_MINUTES,
    muteNotificationSound: isNotificationSoundMuted(),
  });
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushUnsupported, setPushUnsupported] = useState(false);
  const [reminderMode, setReminderMode] = useState('preset');
  const [reminderPresetMinutes, setReminderPresetMinutes] = useState(DEFAULT_REMINDER_MINUTES);
  const [customReminderValue, setCustomReminderValue] = useState(24);
  const [customReminderUnit, setCustomReminderUnit] = useState('hours');
  const [projectReminderMode, setProjectReminderMode] = useState('preset');
  const [projectReminderPresetMinutes, setProjectReminderPresetMinutes] = useState(DEFAULT_REMINDER_MINUTES);
  const [customProjectReminderValue, setCustomProjectReminderValue] = useState(24);
  const [customProjectReminderUnit, setCustomProjectReminderUnit] = useState('hours');
  const [adminCreds, setAdminCreds] = useState({ email: '', password: '' });
  const [adminError, setAdminError] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState({ visible: false, message: 'Saving settings...' });
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [prefsTab, setPrefsTab] = useState('appearance');
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const previewCtrlRef = useRef(null);

  // Stop any in-progress voice preview (cloud audio or browser speech)
  const stopAiPreview = () => {
    try {
      const ctrl = previewCtrlRef.current;
      if (ctrl) {
        if (typeof ctrl.cancel === 'function') ctrl.cancel();
        else if (typeof ctrl === 'function') ctrl();
      }
    } catch {}
    previewCtrlRef.current = null;
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {}
    try { document.querySelectorAll('audio').forEach((a) => { try { a.pause(); } catch {} }); } catch {}
    setPreviewPlaying(false);
  };

  // Cancel preview when the component unmounts
  useEffect(() => () => {
    try {
      const ctrl = previewCtrlRef.current;
      if (ctrl) {
        if (typeof ctrl.cancel === 'function') ctrl.cancel();
        else if (typeof ctrl === 'function') ctrl();
      }
    } catch {}
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {}
  }, []);

  const showSaveFeedback = (message) => {
    setSaveFeedback({ visible: true, message });
  };

  const hideSaveFeedback = () => {
    setSaveFeedback({ visible: false, message: 'Saving settings...' });
  };

  // Keep local component state in sync with Redux settings when they load/update
  useEffect(() => {
    if (settingsState && settingsState.profile) {
      setProfile(prev => ({
        ...prev,
        ...settingsState.profile,
        avatarPreview: settingsState.profile.avatar || prev.avatarPreview
      }));
    }
    if (settingsState && settingsState.company) {
      setCompany(prev => ({
        ...prev,
        ...settingsState.company,
        logoPreview: settingsState.company.logo || prev.logoPreview
      }));
      if (settingsState.company.logoMode === 'landscape' || settingsState.company.logoMode === 'square') {
        setCompanyLogoMode(settingsState.company.logoMode);
      }
    }
    if (settingsState && settingsState.bank) {
      setBank(prev => ({
        ...prev,
        ...settingsState.bank,
        upiQrPreview: settingsState.bank.upiQr || prev.upiQrPreview
      }));
    }
    if (settingsState && settingsState.notifications) {
      // Overall notifications are mandatory and cannot be disabled by the user.
      const incoming = {
        overall: true,
        events: true,
        eventRemindersEnabled: true,
        eventReminderLeadMinutes: DEFAULT_REMINDER_MINUTES,
        projectRemindersEnabled: true,
        projectReminderLeadMinutes: DEFAULT_REMINDER_MINUTES,
        muteNotificationSound: false,
        ...settingsState.notifications,
        overall: true,
      };
      const leadMinutes = clampReminderMinutes(incoming.eventReminderLeadMinutes);
      const isPreset = REMINDER_PRESET_OPTIONS.some((item) => item.value === leadMinutes);
      const projectLeadMinutes = clampReminderMinutes(incoming.projectReminderLeadMinutes);
      const isProjectPreset = REMINDER_PRESET_OPTIONS.some((item) => item.value === projectLeadMinutes);

      setNotifications({
        ...incoming,
        eventRemindersEnabled: incoming.eventRemindersEnabled !== false,
        eventReminderLeadMinutes: leadMinutes,
        projectRemindersEnabled: incoming.projectRemindersEnabled !== false,
        projectReminderLeadMinutes: projectLeadMinutes,
        muteNotificationSound: incoming.muteNotificationSound === true,
      });

      setNotificationSoundMuted(incoming.muteNotificationSound === true);

      if (isPreset) {
        setReminderMode('preset');
        setReminderPresetMinutes(leadMinutes);
      } else {
        const customParts = formatMinutesToCustomParts(leadMinutes);
        setReminderMode('custom');
        setCustomReminderValue(customParts.value);
        setCustomReminderUnit(customParts.unit);
      }

      if (isProjectPreset) {
        setProjectReminderMode('preset');
        setProjectReminderPresetMinutes(projectLeadMinutes);
      } else {
        const customProjectParts = formatMinutesToCustomParts(projectLeadMinutes);
        setProjectReminderMode('custom');
        setCustomProjectReminderValue(customProjectParts.value);
        setCustomProjectReminderUnit(customProjectParts.unit);
      }
    }
  }, [settingsState]);

  // Sync AI preferences from Redux
  useEffect(() => {
    if (aiState?.preferences) {
      setAiPrefs(prev => ({ ...prev, ...aiState.preferences }));
    }
  }, [aiState?.preferences]);

  // Add UPI related state inside bank object (we'll manage with same handler)

  const openModal = (name) => setActiveModal(name);
  const closeModal = () => {
    stopAiPreview();
    setActiveModal(null);
  };

  // Sync push notification state when the notifications modal opens
  useEffect(() => {
    if (activeModal !== 'notifications') return;

    if (!isPushSupported()) {
      setPushUnsupported(true);
      setPushEnabled(false);
      return;
    }
    setPushUnsupported(false);

    let cancelled = false;
    getCurrentPushSubscription()
      .then(async (sub) => {
        if (cancelled) return;
        if (sub) {
          setPushEnabled(true);
          return;
        }
        // Push is ON by default: best-effort auto-subscribe without a prior
        // subscription. Only turn off if the user denied permission.
        const result = await subscribeToPush();
        if (!cancelled) setPushEnabled(!result.denied);
      })
      .catch(() => {
        if (!cancelled) setPushEnabled(true);
      });

    return () => {
      cancelled = true;
    };
  }, [activeModal]);

  const handleTogglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);

    if (pushEnabled) {
      await unsubscribeFromPush();
      setPushEnabled(false);
      showToast('Push notifications turned off for this device', { type: 'info' });
    } else {
      const result = await subscribeToPush();
      if (result.subscribed) {
        setPushEnabled(true);
        showToast('Push notifications enabled — reminders will arrive even when the app is closed', { type: 'success' });
      } else if (result.denied || result.granted === false) {
        showToast('Notification permission is blocked. Allow notifications in your browser/device settings.', { type: 'error', duration: 6000 });
      } else if (result.error === 'no-vapid-key') {
        showToast('Push is not configured on the server yet (VAPID keys missing).', { type: 'error', duration: 6000 });
      } else {
        showToast('Could not enable push notifications on this browser.', { type: 'error' });
      }
    }

    setPushBusy(false);
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file)) {
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const preview = ev.target.result;
      setProfile(prev => ({ ...prev, avatar: file, avatarPreview: preview }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setProfile(prev => ({ ...prev, avatar: null, avatarPreview: null }));
  };

  const handleCompanyChange = (field, value) => {
    setCompany(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file)) {
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const preview = ev.target.result;
      setCompany(prev => ({ ...prev, logo: file, logoPreview: preview }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setCompany(prev => ({ ...prev, logo: null, logoPreview: null }));
  };

  const handleSaveCompany = async () => {
    const safeCompany = { ...company };
    safeCompany.logoMode = companyLogoMode;
    try {
      if (safeCompany.logo instanceof File) {
        showSaveFeedback('Uploading company logo...');
        const logoUrl = await uploadFile(safeCompany.logo, '/uploads/company-logo');
        safeCompany.logo = logoUrl || null;
      } else if (!safeCompany.logo) {
        safeCompany.logo = null;
      }

      delete safeCompany.logoPreview;
      showSaveFeedback('Saving company settings...');
      dispatch(updateCompany(safeCompany));
      await dispatch(saveCompany(safeCompany)).unwrap();
      console.log('Save company', safeCompany);
      closeModal();
    } catch (error) {
      console.error('Save company failed', error);
      if (error.isUploadError) {
        showToast(`Company logo upload failed: ${error.message}`, { type: 'error', duration: 6000 });
      } else {
        window.alert('Unable to save company settings right now. Please try again.');
      }
    } finally {
      hideSaveFeedback();
    }
  };

  const handleBankChange = (field, value) => {
    setBank(prev => ({ ...prev, [field]: value }));
  };

  const handleUpiQrChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file)) {
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const preview = ev.target.result;
      setBank(prev => ({ ...prev, upiQr: file, upiQrPreview: preview }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveUpiQr = () => {
    setBank(prev => ({ ...prev, upiQr: null, upiQrPreview: null }));
  };

  const handleSaveBank = async () => {
    const safeBank = { ...bank };
    try {
      if (safeBank.upiQr instanceof File) {
        showSaveFeedback('Uploading QR code image...');
        const upiQrUrl = await uploadFile(safeBank.upiQr, '/uploads/upi-qr');
        safeBank.upiQr = upiQrUrl || null;
      } else if (!safeBank.upiQr) {
        safeBank.upiQr = null;
      }

      delete safeBank.upiQrPreview;
      showSaveFeedback('Saving bank settings...');
      dispatch(updateBank(safeBank));
      await dispatch(saveBank(safeBank)).unwrap();
      console.log('Save bank', safeBank);
      closeModal();
    } catch (error) {
      console.error('Save bank failed', error);
      if (error.isUploadError) {
        showToast(`QR image upload failed: ${error.message}`, { type: 'error', duration: 6000 });
      } else {
        window.alert('Unable to save bank settings right now. Please try again.');
      }
    } finally {
      hideSaveFeedback();
    }
  };

  const buildNotificationsPayload = (base) => {
    const computedMinutes = reminderMode === 'custom'
      ? convertToMinutes(customReminderValue, customReminderUnit)
      : clampReminderMinutes(reminderPresetMinutes);
    const computedProjectMinutes = projectReminderMode === 'custom'
      ? convertToMinutes(customProjectReminderValue, customProjectReminderUnit)
      : clampReminderMinutes(projectReminderPresetMinutes);

    // Ensure overall notifications remain enabled even if server had them off
    return {
      ...base,
      overall: true,
      eventRemindersEnabled: base.eventRemindersEnabled !== false,
      eventReminderLeadMinutes: computedMinutes,
      projectRemindersEnabled: base.projectRemindersEnabled !== false,
      projectReminderLeadMinutes: computedProjectMinutes,
      muteNotificationSound: base.muteNotificationSound === true,
    };
  };

  const persistNotificationPrefs = async (payload) => {
    try {
      dispatch(updateNotifications(payload));
      await dispatch(saveNotifications(payload)).unwrap();
    } catch (error) {
      console.error('Failed to auto-save notification preference', error);
    }
  };

  // Muting stops every sound in the project at once — toasts, alerts and
  // any assistant voice currently playing.
  const applyMuteSideEffects = (muted) => {
    setNotificationSoundMuted(muted);
    if (muted) {
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {}
      try { document.querySelectorAll('audio').forEach((a) => { try { a.pause(); } catch {} }); } catch {}
    }
  };

  const handleNotificationChange = (field, value) => {
    // Prevent changing the mandatory overall flag from the UI
    if (field === 'overall') return;
    const next = { ...notifications, [field]: value };
    setNotifications(next);
    // Toggle switches apply instantly — no Save click needed for them to
    // take effect (lead-time selects + AI prefs still use the Save button).
    // So turning Record-created OFF immediately stops those notifications.
    if (field === 'muteNotificationSound') applyMuteSideEffects(value === true);
    persistNotificationPrefs(buildNotificationsPayload(next));
  };

  const handleSaveNotifications = async () => {
    // Ensure overall notifications remain enabled even if server had them off
    const payload = buildNotificationsPayload(notifications);
    applyMuteSideEffects(payload.muteNotificationSound);
    try {
      showSaveFeedback('Saving settings...');
      // Save notification settings
      dispatch(updateNotifications(payload));
      await dispatch(saveNotifications(payload)).unwrap();
      // Save AI preferences
      await dispatch(saveAiPreferences(aiPrefs)).unwrap();
      console.log('Save notifications', payload);
      console.log('Save AI prefs', aiPrefs);
      closeModal();
    } catch (error) {
      console.error('Save failed', error);
      window.alert('Unable to save settings right now. Please try again.');
    } finally {
      hideSaveFeedback();
    }
  };

  const handleAiPrefsChange = (field, value) => {
    // Changing tone / language / voice invalidates any in-progress preview
    stopAiPreview();
    setAiPrefs(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveAiPrefs = async () => {
    try {
      showSaveFeedback('Saving AI preferences...');
      await dispatch(saveAiPreferences(aiPrefs)).unwrap();
      showToast('AI preferences saved successfully!', { type: 'success' });
      closeModal();
    } catch (error) {
      console.error('Save AI preferences failed', error);
      showToast('Unable to save AI preferences. Please try again.', { type: 'error' });
    } finally {
      hideSaveFeedback();
    }
  };

  const handleSaveProfile = async () => {
    const safeProfile = { ...profile };
    try {
      if (safeProfile.avatar instanceof File) {
        showSaveFeedback('Uploading profile image...');
        const avatarUrl = await uploadFile(safeProfile.avatar, '/uploads/profile-image');
        safeProfile.avatar = avatarUrl || null;
      } else if (!safeProfile.avatar) {
        safeProfile.avatar = null;
      }

      delete safeProfile.avatarPreview;
      console.log('Saving profile - safeProfile to send:', safeProfile);
      showSaveFeedback('Saving profile settings...');
      dispatch(updateProfile(safeProfile));
      await dispatch(saveProfile(safeProfile)).unwrap();
      // Ensure auth user state reflects profile avatar changes so Navbar updates immediately
      try {
        dispatch(updateUser({ avatar: safeProfile.avatar }));
      } catch (e) {}
      closeModal();
    } catch (error) {
      console.error('Save profile failed', error);
      if (error.isUploadError) {
        showToast(`Profile image upload failed: ${error.message}`, { type: 'error', duration: 6000 });
      } else {
        window.alert('Unable to save profile settings right now. Please try again.');
      }
    } finally {
      hideSaveFeedback();
    }
  };

  const handleSaveSecurity = () => {
    console.log('Save security settings');
    closeModal();
  };

  const fetchActiveSessions = async () => {
    setSessionsLoading(true);
    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/security/sessions`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setActiveSessions(data);
      }
    } catch (err) {
      console.error('Failed to fetch sessions', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      });
      if (res.ok) {
        setActiveSessions(prev => prev.filter(s => s._id !== sessionId));
        showToast('Session revoked successfully', { type: 'success' });
      } else {
        showToast('Failed to revoke session', { type: 'error' });
      }
    } catch (err) {
      showToast('Failed to revoke session', { type: 'error' });
    }
  };

  const getDeviceIcon = (ua) => {
    if (!ua) return <FaLaptop />;
    if (/android|iphone|ipad|ipod/i.test(ua)) return <FaMobileAlt />;
    return <FaLaptop />;
  };

  // Security action handlers (placeholders to wire up later)
  const navigate = useNavigate();

  const handleLogin = async () => {
    closeModal();
    try { await dispatch(logoutUser()); } catch (e) {}
    navigate('/login');
  };

  const handleForgotPassword = async () => {
    closeModal();
    try { await dispatch(logoutUser()); } catch (e) {}
    navigate('/forgot-password');
  };

  const handleAdminVerify = async () => {
    setAdminError('');
    if (!adminCreds.email || !adminCreds.password) {
      setAdminError('Email and password required');
      return;
    }

    try {
      const token = session.getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE_URL}/auth/verify-admin`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(adminCreds)
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        closeModal();
        setTimeout(() => navigate('/admin/dashboard'), 100);
      } else if (data.locked) {
        setAdminError(data.message || 'Invalid admin credentials.');
        const errorMsg = data.message || 'Your account has been locked for security.';
        setTimeout(async () => {
          try { await dispatch(logoutUser()); } catch (e) {}
          navigate('/login');
        }, 2000);
      } else {
        setAdminError(data.message || 'Verification failed.');
      }
    } catch (err) {
      setAdminError('Verification failed. Server unreachable.');
    }
  };


  return (
    <div className="settings">
      <LoadingOverlay visible={saveFeedback.visible} message={saveFeedback.message} />
      <h1 className="settings__title">{t('settings.title')}</h1>
      <div className="settings__cards">
        {/* Accessible cards - shown first for free trial and silver */}
        <div className="settings__card" onClick={() => openModal('profile')} role="button" tabIndex={0}>
          <div className="settings__card-icon settings__card-icon--blue"><FaUser /></div>
          <div className="settings__card-body">
            <h3>{t('settings.yourProfile')}</h3>
            <p>{t('settings.yourProfileDesc')}</p>
          </div>
          <span className="settings__card-arrow">&#8250;</span>
        </div>

        <div className="settings__card" onClick={() => openModal('notifications')} role="button" tabIndex={0}>
          <div className="settings__card-icon settings__card-icon--violet"><FaBell /></div>
          <div className="settings__card-body">
            <h3>{t('settings.yourPreferences')}</h3>
            <p>{t('settings.yourPreferencesDesc')}</p>
          </div>
          <span className="settings__card-arrow">&#8250;</span>
        </div>

        <div className="settings__card" onClick={() => { openModal('security'); fetchActiveSessions(); }} role="button" tabIndex={0}>
          <div className="settings__card-icon settings__card-icon--rose"><FaShieldAlt /></div>
          <div className="settings__card-body">
            <h3>{t('settings.security')}</h3>
            <p>{t('settings.securityDesc')}</p>
          </div>
          <span className="settings__card-arrow">&#8250;</span>
        </div>

        {/* Gold-only cards - shown last for free trial and silver */}
        <div 
          className={`settings__card ${isRestricted ? 'settings__card--restricted' : ''}`} 
          onClick={() => isRestricted ? handleRestrictedCardClick('Company Information') : openModal('company')} 
          role="button" 
          tabIndex={0}
        >
          <div className="settings__card-icon settings__card-icon--emerald"><FaBuilding /></div>
          <div className="settings__card-body">
            <div className="settings__card-header">
              <h3>{t('settings.companyInfo')}</h3>
              {isRestricted && <FaCrown className="settings__gold-badge" title="Gold Plan Feature" />}
            </div>
            <p>{t('settings.companyInfoDesc')}</p>
          </div>
          <span className="settings__card-arrow">&#8250;</span>
        </div>

        <div 
          className={`settings__card ${isRestricted ? 'settings__card--restricted' : ''}`} 
          onClick={() => isRestricted ? handleRestrictedCardClick('Bank Details') : openModal('bank')} 
          role="button" 
          tabIndex={0}
        >
          <div className="settings__card-icon settings__card-icon--amber"><FaUniversity /></div>
          <div className="settings__card-body">
            <div className="settings__card-header">
              <h3>{t('settings.bankDetails')}</h3>
              {isRestricted && <FaCrown className="settings__gold-badge" title="Gold Plan Feature" />}
            </div>
            <p>{t('settings.bankDetailsDesc')}</p>
          </div>
          <span className="settings__card-arrow">&#8250;</span>
        </div>

        {/* Super Admin - Admin/Superadmin only */}
        {isAdmin && (
          <div 
            className="settings__card" 
            onClick={() => openModal('admin')} 
            role="button" 
            tabIndex={0}
          >
            <div className="settings__card-icon settings__card-icon--slate"><FaUserCog /></div>
            <div className="settings__card-body">
              <h3>{t('settings.superAdmin')}</h3>
              <p>{t('settings.superAdminDesc')}</p>
            </div>
            <span className="settings__card-arrow">&#8250;</span>
          </div>
        )}

      </div>

      {/* Modals for each card */}
      <Modal isOpen={activeModal === 'profile'} onClose={closeModal} title={t('settings.profileModalTitle')} size="medium">
        <div className="settings__profile-vertical">
          <div className="settings__avatar-center">
            <div className="avatar-preview">
              {profile.avatarPreview ? (
                <>
                  <img src={profile.avatarPreview} alt="Avatar preview" />
                  <button
                    type="button"
                    className="avatar-remove-btn"
                    onClick={handleRemoveAvatar}
                    title="Remove uploaded image"
                  >
                    <FaTrashAlt />
                  </button>
                </>
              ) : (
                <div className="avatar-placeholder">{t('settings.noImage')}</div>
              )}
            </div>
            <label className="avatar-upload">
              <input type="file" accept="image/*" onChange={handleAvatarChange} />
              <FaCamera /><span>{profile.avatarPreview ? t('profile.changePhoto') : t('profile.uploadPhoto')}</span>
            </label>
            <p className="settings__avatar-center__hint">JPG, PNG, JPEG · Max 5 MB</p>
          </div>

          <div className="settings__fields-vertical">
            <div className="settings__form-group">
              <label>{t('profile.fullName')}</label>
              <input value={profile.fullName} onChange={(e) => handleProfileChange('fullName', e.target.value)} type="text" placeholder="Enter your full name" />
            </div>
            <div className="settings__form-group">
              <label>{t('profile.email')}</label>
              <input value={profile.email} onChange={(e) => handleProfileChange('email', e.target.value)} type="email" placeholder="Enter your email" />
            </div>
            <div className="settings__form-group">
              <label>{t('profile.phone')}</label>
              <input value={profile.phone} onChange={(e) => handleProfileChange('phone', e.target.value)} type="tel" placeholder="Enter your phone number" />
            </div>
            <div className="settings__form-group">
              <label>{t('profile.designation')}</label>
              <input value={profile.designation} onChange={(e) => handleProfileChange('designation', e.target.value)} type="text" placeholder="Enter your designation or role" />
            </div>
          </div>
        </div>
        <div className="settings__modal-footer">
          <div className="settings__modal-left">
            <button className="btn" onClick={closeModal}>{t('common.cancel')}</button>
          </div>
          <div className="settings__modal-right">
            <button className="btn btn--primary" onClick={handleSaveProfile}>{t('common.save')}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'company'} onClose={closeModal} title={t('settings.companyInfo')} size="medium">
        <div className="settings__company-vertical">
          <div className="settings__avatar-center">
            <div className={`avatar-preview ${companyLogoMode === 'landscape' ? 'avatar-preview--landscape' : 'avatar-preview--square'}`}>
              {company.logoPreview ? (
                <>
                  <img src={company.logoPreview} alt="Company logo preview" />
                  <button
                    type="button"
                    className="avatar-remove-btn"
                    onClick={handleRemoveLogo}
                    title="Remove uploaded logo"
                  >
                    <FaTrashAlt />
                  </button>
                </>
              ) : (
                <div className="avatar-placeholder">No logo</div>
              )}
            </div>

            <div className="avatar-upload-row">
              <button
                type="button"
                className={`avatar-size-btn ${companyLogoMode === 'square' ? 'active' : ''}`}
                onClick={() => setCompanyLogoMode('square')}
                title="Square (1:1)"
              >
                1:1
              </button>

              <label className="avatar-upload">
                <input type="file" accept="image/*" onChange={handleLogoChange} />
                <FaCamera /><span>{company.logoPreview ? 'Change logo' : 'Upload logo'}</span>
              </label>

              <button
                type="button"
                className={`avatar-size-btn ${companyLogoMode === 'landscape' ? 'active' : ''}`}
                onClick={() => setCompanyLogoMode('landscape')}
                title="Landscape (16:9)"
              >
                16:9
              </button>
            </div>

            <p className="settings__avatar-center__hint">JPG, PNG, JPEG · Max 5 MB</p>
          </div>

          <div className="settings__fields-vertical">
            <div className="settings__form-group">
              <label>{t('settings.companyName')}</label>
              <input value={company.name} onChange={(e) => handleCompanyChange('name', e.target.value)} type="text" placeholder="Enter company name" />
            </div>
            <div className="settings__form-group">
              <label>{t('settings.companyEmail')}</label>
              <input value={company.email} onChange={(e) => handleCompanyChange('email', e.target.value)} type="email" placeholder="Enter company email" />
            </div>
            <div className="settings__form-group">
              <label>{t('settings.companyPhone')}</label>
              <input value={company.phone} onChange={(e) => handleCompanyChange('phone', e.target.value)} type="tel" placeholder="Enter company phone" />
            </div>
            <div className="settings__form-group">
              <label>{t('settings.companyAddress')}</label>
              <textarea value={company.address} onChange={(e) => handleCompanyChange('address', e.target.value)} placeholder="Enter company address" rows="3"></textarea>
            </div>
            <div className="settings__form-group">
              <label>{t('settings.companyGstin')}</label>
              <input value={company.gstin} onChange={(e) => handleCompanyChange('gstin', e.target.value)} type="text" placeholder="Enter GSTIN" />
            </div>
          </div>
        </div>
        <div className="settings__modal-footer">
          <div className="settings__modal-left">
            <button className="btn" onClick={closeModal}>{t('common.cancel')}</button>
          </div>
          <div className="settings__modal-right">
            <button className="btn btn--primary" onClick={handleSaveCompany}>{t('common.save')}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'bank'} onClose={closeModal} title={t('settings.bankDetails')} size="medium">
        <div className="settings__form-group">
          <label>{t('settings.bankName')}</label>
          <input value={bank.bankName} onChange={(e) => handleBankChange('bankName', e.target.value)} type="text" placeholder="Enter bank name" />
        </div>
        <div className="settings__form-group">
          <label>{t('settings.accountHolder')}</label>
          <input value={bank.accountHolder} onChange={(e) => handleBankChange('accountHolder', e.target.value)} type="text" placeholder="Enter account holder name" />
        </div>
        <div className="settings__form-group">
          <label>{t('settings.accountNumber')}</label>
          <input value={bank.accountNumber} onChange={(e) => handleBankChange('accountNumber', e.target.value)} type="text" placeholder="Enter account number" />
        </div>
        <div className="settings__form-group">
          <label>{t('settings.branch')}</label>
          <input value={bank.branch} onChange={(e) => handleBankChange('branch', e.target.value)} type="text" placeholder="Enter branch name" />
        </div>
        <div className="settings__form-group">
          <label>{t('settings.ifsc')}</label>
          <input value={bank.ifsc} onChange={(e) => handleBankChange('ifsc', e.target.value)} type="text" placeholder="Enter IFSC code" />
        </div>
        <div className="settings__form-group">
          <label>{t('settings.companyAddress')}</label>
          <textarea value={bank.address} onChange={(e) => handleBankChange('address', e.target.value)} placeholder="Enter branch address" rows="3"></textarea>
        </div>
        <div className="settings__form-group">
          <label>{t('settings.upiId')}</label>
          <input value={bank.upiId || ''} onChange={(e) => handleBankChange('upiId', e.target.value)} type="text" placeholder="Enter UPI ID (example@bank)" />
        </div>

          <div className="avatar-row">
            <p className="avatar-row__label">{t('settings.upiQr')}</p>
            <div className="avatar-preview avatar-preview--qr">
              {bank.upiQrPreview ? (
                <>
                  <img src={bank.upiQrPreview} alt="UPI QR preview" />
                  <button
                    type="button"
                    className="avatar-remove-btn"
                    onClick={handleRemoveUpiQr}
                    title="Remove uploaded QR"
                  >
                    <FaTrashAlt />
                  </button>
                </>
              ) : (
                <div className="avatar-placeholder">{t('settings.noQR')}</div>
              )}
            </div>
            <label className="avatar-upload">
              <input type="file" accept="image/*" onChange={handleUpiQrChange} />
              <FaCamera /><span>{bank.upiQrPreview ? t('settings.changeQR') : t('settings.uploadQR')}</span>
            </label>
          </div>
        
        <div className="settings__modal-footer">
          <div className="settings__modal-left">
            <button className="btn" onClick={closeModal}>{t('common.cancel')}</button>
          </div>
          <div className="settings__modal-right">
            <button className="btn btn--primary" onClick={handleSaveBank}>{t('common.save')}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'notifications'} onClose={closeModal} title={t('settings.yourPreferences')} size="medium">
        {/* Tab Bar */}
        <div className="settings__prefs-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={prefsTab === 'appearance'}
            className={`settings__prefs-tab ${prefsTab === 'appearance' ? 'settings__prefs-tab--active' : ''}`}
            onClick={() => setPrefsTab('appearance')}
          >
            <span className="settings__prefs-tab-icon"><FaPaintBrush /></span>
            <span className="settings__prefs-tab-label">{t('appearance.title')}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={prefsTab === 'notifications'}
            className={`settings__prefs-tab ${prefsTab === 'notifications' ? 'settings__prefs-tab--active' : ''}`}
            onClick={() => setPrefsTab('notifications')}
          >
            <span className="settings__prefs-tab-icon"><FaBell /></span>
            <span className="settings__prefs-tab-label">{t('settings.notificationsTab')}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={prefsTab === 'reminders'}
            className={`settings__prefs-tab ${prefsTab === 'reminders' ? 'settings__prefs-tab--active' : ''}`}
            onClick={() => setPrefsTab('reminders')}
          >
            <span className="settings__prefs-tab-icon"><FaClock /></span>
            <span className="settings__prefs-tab-label">{t('settings.remindersTab')}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={prefsTab === 'ai'}
            className={`settings__prefs-tab ${prefsTab === 'ai' ? 'settings__prefs-tab--active' : ''}`}
            onClick={() => setPrefsTab('ai')}
          >
            <span className="settings__prefs-tab-icon"><FaRobot /></span>
            <span className="settings__prefs-tab-label">{t('settings.aiAssistant')}</span>
          </button>
        </div>

        {/* Tab: Appearance & Language */}
        {prefsTab === 'appearance' && (
          <div className="settings__prefs-panel">
            {/* Theme segmented control */}
            <div className="settings__app-block">
              <div className="settings__app-block-head">
                <span className="settings__app-block-icon"><FaSun /></span>
                <span className="settings__app-block-title">{t('appearance.title')}</span>
              </div>
              <div className="settings__seg" role="radiogroup" aria-label={t('appearance.title')}>
                <button
                  type="button"
                  className={`settings__seg-opt ${themeMode === 'light' ? 'settings__seg-opt--active' : ''}`}
                  onClick={() => setThemeMode('light')}
                  role="radio"
                  aria-checked={themeMode === 'light'}
                >
                  <FaSun /> {t('appearance.light')}
                </button>
                <button
                  type="button"
                  className={`settings__seg-opt ${themeMode === 'dark' ? 'settings__seg-opt--active' : ''}`}
                  onClick={() => setThemeMode('dark')}
                  role="radio"
                  aria-checked={themeMode === 'dark'}
                >
                  <FaMoon /> {t('appearance.dark')}
                </button>
                <button
                  type="button"
                  className={`settings__seg-opt ${themeMode === 'system' ? 'settings__seg-opt--active' : ''}`}
                  onClick={() => setThemeMode('system')}
                  role="radio"
                  aria-checked={themeMode === 'system'}
                >
                  <FaDesktop /> {t('appearance.system')}
                </button>
              </div>
              <small className="settings__helper-text">{t('appearance.systemHint')}</small>
            </div>

            {/* Language segmented control */}
            <div className="settings__app-block">
              <div className="settings__app-block-head">
                <span className="settings__app-block-icon"><FaGlobe /></span>
                <span className="settings__app-block-title">{t('settings.languagePref')}</span>
              </div>
              <div className="settings__seg" role="radiogroup" aria-label={t('settings.languagePref')}>
                <button
                  type="button"
                  className={`settings__seg-opt ${language === 'en' ? 'settings__seg-opt--active' : ''}`}
                  onClick={() => setLanguage('en')}
                  role="radio"
                  aria-checked={language === 'en'}
                >
                  {t('common.english')}
                </button>
                <button
                  type="button"
                  className={`settings__seg-opt ${language === 'kn' ? 'settings__seg-opt--active' : ''}`}
                  onClick={() => setLanguage('kn')}
                  role="radio"
                  aria-checked={language === 'kn'}
                >
                  {t('common.kannada')}
                </button>
              </div>
              <small className="settings__helper-text">{t('settings.languagePrefDesc')}</small>
            </div>

            {/* Download App */}
            <div className="settings__install-cta">
              <div className="settings__install-cta-header">
                <FaMobileAlt />
                <h4>{t('settings.installApp')}</h4>
              </div>
              <p>{t('settings.installApplyDesc')}</p>
              <div className="settings__install-cta-actions">
                <a href="/MIVENT.apk" className="btn btn--primary" download>
                  <FaDownload /> {t('settings.downloadApk')}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Notifications */}
        {prefsTab === 'notifications' && (
          <div className="settings__prefs-panel">
            <div className="settings__form-group">
              <label className="settings__switch-label">
                <span className="settings__switch-text">{t('notifications.overallNotifications')}</span>
                <span className="settings__switch">
                  <input type="checkbox" checked={notifications.overall} disabled title="Overall notifications are always enabled" />
                  <span className="settings__switch-slider" />
                </span>
              </label>
            </div>
            <div className="settings__form-group settings__push-group">
              <label className="settings__push-label">{t('notifications.pushTitle')}</label>
              <p className="settings__helper-text">{t('notifications.pushDesc')}</p>
              {pushUnsupported ? (
                <p className="settings__helper-text settings__push-warning">{t('notifications.pushUnsupported')}</p>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleTogglePush}
                  disabled={pushBusy}
                >
                  {pushBusy
                    ? t('notifications.pushBusy')
                    : pushEnabled
                      ? t('notifications.pushDisable')
                      : t('notifications.pushEnable')}
                </button>
              )}
            </div>
            <div className="settings__form-group">
              <label className="settings__switch-label">
                <span className="settings__switch-text">{t('notifications.muteSound')}</span>
                <span className="settings__switch">
                  <input
                    type="checkbox"
                    checked={notifications.muteNotificationSound === true}
                    onChange={(e) => handleNotificationChange('muteNotificationSound', e.target.checked)}
                  />
                  <span className="settings__switch-slider" />
                </span>
              </label>
            </div>
            <div className="settings__form-group">
              <label className="settings__switch-label">
                <span className="settings__switch-text">{t('notifications.eventNotifications')}</span>
                <span className="settings__switch">
                  <input type="checkbox" checked={notifications.events} onChange={(e) => handleNotificationChange('events', e.target.checked)} />
                  <span className="settings__switch-slider" />
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Tab: Reminders */}
        {prefsTab === 'reminders' && (
          <div className="settings__prefs-panel">
            <div className="settings__form-group">
              <label className="settings__switch-label">
                <span className="settings__switch-text">{t('notifications.eventReminder')}</span>
                <span className="settings__switch">
                  <input
                    type="checkbox"
                    checked={notifications.eventRemindersEnabled !== false}
                    onChange={(e) => handleNotificationChange('eventRemindersEnabled', e.target.checked)}
                  />
                  <span className="settings__switch-slider" />
                </span>
              </label>
            </div>
            <div className="settings__form-group">
              <label>{t('notifications.remindBefore')}</label>
              <CustomSelect
                id="event-reminder-preset"
                value={reminderMode === 'preset' ? reminderPresetMinutes : 'custom'}
                disabled={notifications.eventRemindersEnabled === false}
                onChange={(value) => {
                  if (value === 'custom') {
                    setReminderMode('custom');
                    return;
                  }
                  setReminderMode('preset');
                  setReminderPresetMinutes(clampReminderMinutes(Number(value)));
                }}
                options={[
                  ...REMINDER_PRESET_OPTIONS.map((preset) => ({
                    value: preset.value,
                    label: t(`reminder.preset_${preset.value}`),
                  })),
                  { value: 'custom', label: t('notifications.custom') },
                ]}
              />
              {reminderMode === 'custom' && (
                <div className="settings__reminder-custom-row">
                  <input
                    type="number"
                    min="1"
                    max={customReminderUnit === 'days' ? 15 : customReminderUnit === 'hours' ? 360 : MAX_REMINDER_MINUTES}
                    value={customReminderValue}
                    onChange={(e) => setCustomReminderValue(e.target.value)}
                    disabled={notifications.eventRemindersEnabled === false}
                  />
                  <CustomSelect
                    id="event-reminder-unit"
                    value={customReminderUnit}
                    disabled={notifications.eventRemindersEnabled === false}
                    onChange={(v) => setCustomReminderUnit(v)}
                    options={[
                      { value: 'minutes', label: t('notifications.minutes') },
                      { value: 'hours', label: t('notifications.hours') },
                      { value: 'days', label: t('notifications.days') },
                    ]}
                  />
                </div>
              )}
              <small className="settings__helper-text">{t('notifications.customHint')}</small>
            </div>

            <hr className="settings__divider" />

            <div className="settings__form-group">
              <label className="settings__switch-label">
                <span className="settings__switch-text">{t('notifications.projectReminder')}</span>
                <span className="settings__switch">
                  <input
                    type="checkbox"
                    checked={notifications.projectRemindersEnabled !== false}
                    onChange={(e) => handleNotificationChange('projectRemindersEnabled', e.target.checked)}
                  />
                  <span className="settings__switch-slider" />
                </span>
              </label>
            </div>
            <div className="settings__form-group">
              <label>{t('notifications.remindProjectBefore')}</label>
              <CustomSelect
                id="project-reminder-preset"
                value={projectReminderMode === 'preset' ? projectReminderPresetMinutes : 'custom'}
                disabled={notifications.projectRemindersEnabled === false}
                onChange={(value) => {
                  if (value === 'custom') {
                    setProjectReminderMode('custom');
                    return;
                  }
                  setProjectReminderMode('preset');
                  setProjectReminderPresetMinutes(clampReminderMinutes(Number(value)));
                }}
                options={[
                  ...REMINDER_PRESET_OPTIONS.map((preset) => ({
                    value: preset.value,
                    label: t(`reminder.preset_${preset.value}`),
                  })),
                  { value: 'custom', label: t('notifications.custom') },
                ]}
              />
              {projectReminderMode === 'custom' && (
                <div className="settings__reminder-custom-row">
                  <input
                    type="number"
                    min="1"
                    max={customProjectReminderUnit === 'days' ? 15 : customProjectReminderUnit === 'hours' ? 360 : MAX_REMINDER_MINUTES}
                    value={customProjectReminderValue}
                    onChange={(e) => setCustomProjectReminderValue(e.target.value)}
                    disabled={notifications.projectRemindersEnabled === false}
                  />
                  <CustomSelect
                    id="project-reminder-unit"
                    value={customProjectReminderUnit}
                    disabled={notifications.projectRemindersEnabled === false}
                    onChange={(v) => setCustomProjectReminderUnit(v)}
                    options={[
                      { value: 'minutes', label: t('notifications.minutes') },
                      { value: 'hours', label: t('notifications.hours') },
                      { value: 'days', label: t('notifications.days') },
                    ]}
                  />
                </div>
              )}
              <small className="settings__helper-text">{t('notifications.customProjectHint')}</small>
            </div>
          </div>
        )}

        {/* Tab: AI Assistant */}
        {prefsTab === 'ai' && (
          <div className="settings__prefs-panel">
            <p className="settings__ai-prefs-intro">{t('settings.aiPrefsIntro')}</p>
            <div className="settings__form-group">
              <label>{t('settings.aiTone')}</label>
              <CustomSelect
                value={aiPrefs.tone}
                onChange={(v) => handleAiPrefsChange('tone', v)}
                id="ai-tone-select"
                options={[
                  { value: 'friendly', label: t('settings.aiToneFriendly') },
                  { value: 'professional', label: t('settings.aiToneProfessional') },
                  { value: 'humorous', label: t('settings.aiToneHumorous') },
                  { value: 'minimalist', label: t('settings.aiToneMinimalist') },
                  { value: 'enthusiastic', label: t('settings.aiToneEnthusiastic') },
                ]}
              />
            </div>
            <div className="settings__form-group">
              <label>{t('settings.aiSpeechLang')}</label>
              <CustomSelect
                value={aiPrefs.speechLanguage}
                onChange={(v) => handleAiPrefsChange('speechLanguage', v)}
                id="ai-lang-select"
                options={[
                  { value: 'en-IN', label: 'English (India)' },
                  { value: 'kn-IN', label: 'ಕನ್ನಡ (Kannada)' },
                ]}
              />
            </div>
            <div className="settings__form-group">
              <label>{t('settings.aiVoice')}</label>
              <CustomSelect
                value={aiPrefs.assistantVoice || 'female'}
                onChange={(v) => handleAiPrefsChange('assistantVoice', v)}
                id="ai-voice-select"
                options={[
                  { value: 'female', label: t('settings.aiVoiceFemale') },
                  { value: 'male', label: t('settings.aiVoiceMale') },
                ]}
              />
              {(() => {
                const toneKey = AI_VOICE_PREVIEWS[aiPrefs.tone] ? aiPrefs.tone : 'professional';
                const toneLabelMap = {
                  friendly: t('settings.aiToneFriendly'),
                  professional: t('settings.aiToneProfessional'),
                  humorous: t('settings.aiToneHumorous'),
                  minimalist: t('settings.aiToneMinimalist'),
                  enthusiastic: t('settings.aiToneEnthusiastic'),
                };
                const previewLine = getAiPreviewLine(aiPrefs.tone, aiPrefs.speechLanguage, aiPrefs.assistantVoice);
                const voiceName = aiPrefs.assistantVoice === 'male' ? 'Mivan' : 'Mira';
                const langName = aiPrefs.speechLanguage === 'kn-IN' ? 'ಕನ್ನಡ' : 'English';
                return (
                  <>
                    <button
                      type="button"
                      className="btn btn--sm"
                      style={{ marginTop: '0.75rem' }}
                      onClick={async () => {
                        if (previewPlaying) {
                          stopAiPreview();
                          return;
                        }
                        const { isNotificationSoundMuted } = await import('../utils/alertSound');
                        if (isNotificationSoundMuted()) {
                          showToast('Sounds are muted — turn off "Mute all sounds" to preview voice.', { type: 'info' });
                          return;
                        }
                        const { speakWithCloud } = await import('../utils/cloudSpeech');
                        stopAiPreview();
                        setPreviewPlaying(true);
                        const ctrl = speakWithCloud(previewLine, {
                          assistantVoice: aiPrefs.assistantVoice || 'female',
                          speechLanguage: aiPrefs.speechLanguage || 'en-IN',
                          tone: toneKey,
                          preferCloud: true,
                          onEnd: () => { previewCtrlRef.current = null; setPreviewPlaying(false); },
                          onError: () => { previewCtrlRef.current = null; setPreviewPlaying(false); },
                        });
                        previewCtrlRef.current = ctrl;
                      }}
                    >
                      {previewPlaying
                        ? <><FaStop /> {language === 'kn' ? 'ನಿಲ್ಲಿಸಿ' : 'Stop'}</>
                        : <><FaVolumeUp /> {t('settings.aiPreviewVoice')} — {voiceName} · {langName} · {toneLabelMap[toneKey]}</>}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        <div className="settings__modal-footer">
          <div className="settings__modal-left">
            <button className="btn" onClick={closeModal}>{t('common.cancel')}</button>
          </div>
          <div className="settings__modal-right">
            <button className="btn btn--primary" onClick={handleSaveNotifications}>{t('common.save')}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'security'} onClose={closeModal} title={t('settings.security')} size="medium">
        {authUser && authUser.email ? (
          <div className="settings__security-user">{t('settings.loggedInAs')}: <strong>{authUser.email}</strong></div>
        ) : null}
        <div className="settings__security-actions">
          <button className="btn btn--login" onClick={handleLogin}><FaMobileAlt /> {t('settings.loginAnother')}</button>
          <button className="btn btn--forgot" onClick={handleForgotPassword}><FaEye /> {t('settings.forgotPassword')}</button>
        </div>
        <div className="settings__modal-footer" style={{ justifyContent: 'center' }}>
          <p className='caution'>{t('settings.logoutWarning')}</p>
        </div>
        <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9rem' }}>Active Sessions</h4>
        <div className="settings__sessions">
          {sessionsLoading ? (
            <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>Loading sessions...</p>
          ) : activeSessions.length === 0 ? (
            <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>No active sessions</p>
          ) : (
            activeSessions.map(s => {
              const device = s.device || '';
              const deviceIcon = /android|iphone|ipad|ipod/i.test(device) ? <FaMobileAlt /> : <FaLaptop />;
              const deviceName = /android/i.test(device) ? 'Android' : /iphone|ipad|ipod/i.test(device) ? 'iOS' : /windows nt/i.test(device) ? 'Windows' : /macintosh|mac os x/i.test(device) ? 'Mac' : /linux/i.test(device) ? 'Linux' : 'Unknown';
              const browser = /edg\//i.test(device) ? 'Edge' : /chrome\//i.test(device) ? 'Chrome' : /firefox\//i.test(device) ? 'Firefox' : /safari\//i.test(device) ? 'Safari' : /opr\//i.test(device) ? 'Opera' : '';
              const lastActive = s.lastActive ? new Date(s.lastActive).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <div key={s._id} className="settings__session-item">
                  <div className="settings__session-device">
                    {deviceIcon}
                    <div>
                      <strong>{s.userId?.name || 'Unknown'}</strong>
                      <span className="settings__session-meta">{deviceName}{browser ? ` · ${browser}` : ''}</span>
                    </div>
                  </div>
                  <span className="settings__session-time">{lastActive}</span>
                  <button className="btn btn--danger btn--sm" onClick={() => handleDeleteSession(s._id)} title="Revoke Session">
                    <FaTrash />
                  </button>
                </div>
              );
            })
          )}
        </div>
        {activeSessions.length > 0 && (
          <button className="btn btn--outline btn--sm" onClick={fetchActiveSessions} style={{ display: 'block', margin: '0.75rem auto' }}>
            Refresh Sessions
          </button>
        )}
      </Modal>

      <Modal isOpen={activeModal === 'admin'} onClose={closeModal} title={t('settings.adminTitle')} size="small">
        <div className="settings__admin-auth">
          <div className="settings__admin-auth-icon">
            <FaUserCog />
          </div>
          <p className="settings__admin-auth-title">{t('settings.authInstruction')}</p>
          <p className="settings__admin-auth-sub">Admin access is required to manage users and system settings.</p>

          <div className="settings__admin-auth-form">
            <div className="settings__form-group">
              <label>{t('settings.adminEmail')}</label>
              <input 
                type="email" 
                placeholder="admin@mivent.com"
                value={adminCreds.email}
                onChange={(e) => setAdminCreds({ ...adminCreds, email: e.target.value })}
              />
            </div>
            
            <div className="settings__form-group">
              <label>{t('settings.adminPassword')}</label>
              <div className="password-input-wrapper">
                <input 
                  type={showAdminPassword ? "text" : "password"} 
                  placeholder="••••••••"
                  value={adminCreds.password}
                  onChange={(e) => setAdminCreds({ ...adminCreds, password: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdminVerify()}
                />
                <button 
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  aria-label={showAdminPassword ? "Hide password" : "Show password"}
                >
                  {showAdminPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {adminError && (
              <div className="settings__admin-error">
                <FaEye style={{ fontSize: '0.75rem', flexShrink: 0 }} />
                <span>{adminError}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="settings__modal-footer">
          <div className="settings__modal-left">
            <button className="btn" onClick={closeModal}>{t('common.cancel')}</button>
          </div>
          <div className="settings__modal-right">
            <button className="btn btn--primary" onClick={handleAdminVerify}>{t('settings.authenticate')}</button>
          </div>
        </div>
      </Modal>



      {/* Plan Upgrade Modal for restricted features */}
      <PlanUpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        featureName={restrictedFeatureName}
        currentPlan={planTier || 'free'}
      />
    </div>
  );
};

export default Settings;
